import { useEffect, useState, useLayoutEffect, useRef, Fragment } from 'react';
import logo from "./assets/snxWhiteLogo.png";
import "./App.css"
import * as am4core from "@amcharts/amcharts4/core";
import * as am4maps from "@amcharts/amcharts4/maps";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import am4geodata_worldLow from "@amcharts/amcharts4-geodata/worldLow";
import ReactCountryFlag from "react-country-flag"

am4core.useTheme(am4themes_animated);
var finalData;
const POLLINGTIMEOUT = 60 * 60 * 1000;

var clearTimmerSync;
var chart;

const SEVERITY_COLORS = {
    critical: '#FF0000',
    high:     '#FF6600',
    medium:   '#FFCC00',
    low:      '#00CC44',
};

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}


// SVG donut chart — uses viewBox so it scales with .tm-donut-wrap CSS size
function DonutChart({ segments, total }) {
    const cx = 55, cy = 55, r = 38, sw = 13;
    const circ = 2 * Math.PI * r;
    let cumPct = 0;
    return (
        <div className="tm-donut-wrap">
            <svg viewBox="0 0 110 110" className="tm-donut-svg">
                {/* Track ring */}
                <circle cx={cx} cy={cy} r={r} fill="none"
                    stroke="rgba(180,20,30,0.18)" strokeWidth={sw} />
                {segments.map((seg, i) => {
                    const dash = (seg.percentage / 100) * circ;
                    const rot  = -90 + (cumPct / 100) * 360;
                    cumPct += seg.percentage;
                    if (dash === 0) return null;
                    return (
                        <circle key={i} cx={cx} cy={cy} r={r}
                            fill="none"
                            stroke={seg.color}
                            strokeWidth={sw}
                            strokeDasharray={`${dash} ${circ - dash}`}
                            style={{
                                transform: `rotate(${rot}deg)`,
                                transformOrigin: `${cx}px ${cy}px`,
                            }}
                        />
                    );
                })}
            </svg>
            <div className="tm-donut-label">
                <div className="tm-donut-total">{total}</div>
                <div className="tm-donut-active">ACTIVE</div>
            </div>
        </div>
    );
}

export default function MalThreatModule() {

    const [finalData1, setFinalData1]   = useState(null);
    const [allPoints, setAllPoints]     = useState([]);
    const [displaylist, setDisplaylist] = useState({});
    const [activeFilter]                = useState('All');
    const [fetchError, setFetchError]   = useState(null);
    // Track feed panel body height so the slider animation starts below the visible area
    const [feedBodyH, setFeedBodyH]       = useState(220);
    const [statsModalOpen, setStatsModalOpen] = useState(false);
    const [animatedStats, setAnimatedStats]   = useState({ total: 0, c2: 0, countries: 0 });
    const [selectedCountry, setSelectedCountry] = useState(null);
    const feedBodyRef                     = useRef(null);
    const touchStartY                     = useRef(0);

    // Orientation change — force amCharts redraw when phone rotates
    useEffect(() => {
        const onOrientationChange = () => {
            setTimeout(() => window.dispatchEvent(new Event('resize')), 150);
        };
        window.addEventListener('orientationchange', onOrientationChange);
        return () => window.removeEventListener('orientationchange', onOrientationChange);
    }, []);

    // KPI count-up animation — fires once when data first loads
    useEffect(() => {
        if (!finalData1) return;
        const targetTotal     = finalData1.display?.summary?.total_events     || 0;
        const targetC2        = (finalData1.display?.['Top Malware Types'] || []).find(m => m.category === 'C2')?.count || 0;
        const targetCountries = finalData1.display?.summary?.unique_countries || 0;
        if (!targetTotal && !targetCountries) return;
        const duration = 1400;
        const start = performance.now();
        let rafId;
        function tick(now) {
            const elapsed = Math.min(now - start, duration);
            const ease = 1 - Math.pow(1 - elapsed / duration, 3);
            setAnimatedStats({
                total:     Math.round(targetTotal     * ease),
                c2:        Math.round(targetC2        * ease),
                countries: Math.round(targetCountries * ease),
            });
            if (elapsed < duration) rafId = requestAnimationFrame(tick);
        }
        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, [finalData1]);

    // ResizeObserver — keeps slider start offset in sync with actual panel height
    useEffect(() => {
        if (!feedBodyRef.current) return;
        const ro = new ResizeObserver(entries => {
            const h = entries[0]?.contentRect?.height;
            if (h && h > 0) setFeedBodyH(Math.round(h));
        });
        ro.observe(feedBodyRef.current);
        return () => ro.disconnect();
    }, []);

    // Data fetch
    useEffect(() => {
        function initAccessFn() {
            clearTimeout(clearTimmerSync);
            fetch(`/api/v1/threatmap`, {
                method: "get",
                headers: {
                    "Content-Type": "application/json",
                },
            }).then(response => {
                if (!response.ok) {
                    const msg = `Server error: ${response.status}`;
                    setFetchError(msg);
                    return;
                }
                setFetchError(null);
                disposeChart();
                response.json().then(data => {
                    finalData = data;
                    for (let x in finalData["plotdots"]) {
                        const pt = finalData["plotdots"][x];
                        if (pt["city"] !== null) {
                            pt["cityRaw"] = pt["city"];
                            pt["city"]    = "(" + pt["city"] + ")";
                        }
                        pt["severityColor"] = SEVERITY_COLORS[pt["severity"]] || '#888888';
                    }
                    setDisplaylist(finalData['display'] || {});
                    setFinalData1(finalData);
                    setAllPoints(finalData.plotdots);
                });
            }).catch(err => {
                setFetchError('Network error: unable to reach the threat map server.');
                console.error(err);
            });
            clearTimmerSync = setTimeout(() => initAccessFn(), POLLINGTIMEOUT);
        }
        initAccessFn();
        return () => clearTimeout(clearTimmerSync);
    }, []);

    // Map initialisation — re-runs when data changes
    useLayoutEffect(() => {
        disposeChart();
        am4core.options.autoDispose = true;
        chart = am4core.create("chartdiv", am4maps.MapChart);
        chart.geodata = am4geodata_worldLow;
        chart.projection = new am4maps.projections.Miller();
        chart.maxZoomLevel = 1;
        chart.background.opacity = 0;
        chart.seriesContainer.draggable = false;
        chart.seriesContainer.resizable = false;

        // ── Dark country polygons ──
        const polySeries = chart.series.push(new am4maps.MapPolygonSeries());
        polySeries.exclude = ["AQ"];
        polySeries.useGeodata = true;
        const polyTpl = polySeries.mapPolygons.template;
        polyTpl.fill              = am4core.color("#561525");
        polyTpl.stroke            = am4core.color("#d4253e");
        polyTpl.strokeWidth       = 0.7;
        polyTpl.polygon.fillOpacity = 1;
        polyTpl.interactionsEnabled = true;
        polyTpl.tooltipText       = "{name}";
        polySeries.tooltip.background.fill        = am4core.color("#1a0205");
        polySeries.tooltip.background.stroke      = am4core.color("#c0182e");
        polySeries.tooltip.background.strokeWidth = 1;
        polySeries.tooltip.label.fill             = am4core.color("#ffffff");
        polySeries.tooltip.label.fontSize         = 12;
        polySeries.tooltip.animationDuration      = 0;
        const polyHover = polyTpl.states.create("hover");
        polyHover.properties.fill = am4core.color("#5c1020");

        // Country click — toggle drilldown panel
        polyTpl.events.on("hit", e => {
            const id   = e.target.dataItem?.dataContext?.id;
            const name = e.target.dataItem?.dataContext?.name;
            if (id && name) {
                setSelectedCountry(prev => prev?.id === id ? null : { id, name });
            }
        });

        // ── Shared tooltip HTML template ──
        const tooltipHTML = `
            <div style='font-family:Helvetica;font-size:10pt;padding:6px 10px;min-width:200px;line-height:1.5;display:flex;align-items:center;gap:10px;'>
                <div style='width:9px;height:9px;border-radius:50%;background:{color};box-shadow:0 0 6px {color};flex-shrink:0;'></div>
                <div style='min-width:0;'>
                    <div style='color:#ffffff;font-weight:bold;font-size:10pt;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;'>
                        {country}&ensp;<span style='color:#aaaaaa;font-weight:normal;font-size:9pt;'>{city}</span>
                    </div>
                    <div style='display:flex;align-items:center;gap:7px;margin-top:3px;'>
                        <span style='background:{severityColor};color:#000;border-radius:3px;padding:1px 7px;font-size:7pt;font-weight:bold;text-transform:uppercase;flex-shrink:0;'>
                            {severity}
                        </span>
                        <span style='color:#cccccc;font-size:9pt;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;'>
                            {strain}
                        </span>
                    </div>
                </div>
            </div>`;

        // ── Active / animated series ──
        const imgSeries = chart.series.push(new am4maps.MapImageSeries());
        imgSeries.mapImages.template.propertyFields.longitude = "longitude";
        imgSeries.mapImages.template.propertyFields.latitude  = "latitude";

        // ── Default / hover series — tooltip on cursor hover ──
        const dfltSeries = chart.series.push(new am4maps.MapImageSeries());
        dfltSeries.tooltip.animationDuration = 1;
        dfltSeries.tooltip.showInViewport    = false;
        dfltSeries.tooltip.background.fillOpacity = 0.95;
        dfltSeries.tooltip.getFillFromObject  = false;
        dfltSeries.tooltip.background.fill    = am4core.color("#1a0205");
        dfltSeries.tooltip.background.stroke  = am4core.color("#7a0a18");
        dfltSeries.mapImages.template.propertyFields.longitude = "longitude";
        dfltSeries.mapImages.template.propertyFields.latitude  = "latitude";
        dfltSeries.mapImages.template.tooltipHTML  = tooltipHTML;
        dfltSeries.mapImages.template.showTooltipOn = "hover";

        // Animated outer ring
        const ring = imgSeries.mapImages.template.createChild(am4core.Circle);
        ring.radius = 5;
        ring.propertyFields.fill = "color";
        ring.fillOpacity = 0.85;

        // Inner dot + pulse trigger
        const dot = imgSeries.mapImages.template.createChild(am4core.Circle);
        dot.radius = 2;
        dot.propertyFields.fill = "color";
        dot.fillOpacity = 1;
        dot.events.on("inited", e => animateBullet(e.target));

        // Static dot for hover series
        const staticDot = dfltSeries.mapImages.template.createChild(am4core.Circle);
        staticDot.radius = 4;
        staticDot.propertyFields.fill = "color";
        staticDot.fillOpacity = 0.75;

        // Normal dots pulse subtly — kept smaller so arc-endpoint ripples stand out
        function animateBullet(circle) {
            const anim = circle.animate(
                [{ property: "scale", from: 0, to: 4 },
                 { property: "opacity", from: 0.6, to: 0.05 }],
                1400, am4core.ease.circleInOut
            );
            anim.events.on("animationended", e => animateBullet(e.target.object));
        }

        let dotRevealDone = false;
        if (finalData1 != null) { initcallback(); }

        async function initcallback() {
            // ── Sequential dot reveal — fires once, concurrent with pulse loop ──
            if (!dotRevealDone) {
                dotRevealDone = true;
                (async () => {
                    for (const pt of finalData1["plotdots"]) {
                        if (!chart || chart.isDisposed()) return;
                        dfltSeries.addData(pt);
                        await sleep(120);
                    }
                })();
            }
            // ── SVG overlay arc animation ──
            const pts = finalData1["plotdots"];
            if (pts && pts.length >= 2) {
                const half = Math.floor(pts.length / 2);
                let arcIdx = 0;

                const chartSvg = document.getElementById("chartdiv")?.querySelector("svg");
                const arcGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
                arcGroup.setAttribute("class", "threat-arc-overlay");
                if (chartSvg) chartSvg.appendChild(arcGroup);

                const svgW = chartSvg?.viewBox?.baseVal?.width  || chartSvg?.clientWidth  || 800;
                const svgH = chartSvg?.viewBox?.baseVal?.height || chartSvg?.clientHeight || 500;

                function drawNextArc() {
                    if (!arcGroup.isConnected) return;

                    const src = pts[arcIdx % pts.length];
                    const dst = pts[(arcIdx + half) % pts.length];
                    arcIdx = (arcIdx + 1) % pts.length;

                    const p1 = chart.geoPointToSVG({ latitude: src.latitude, longitude: src.longitude });
                    const p2 = chart.geoPointToSVG({ latitude: dst.latitude, longitude: dst.longitude });

                    const valid = (p) => p && !isNaN(p.x) && !isNaN(p.y) && p.x > 0 && p.y > 0 && p.x < svgW && p.y < svgH;
                    if (!valid(p1) || !valid(p2)) { setTimeout(drawNextArc, 600); return; }

                    const mx   = (p1.x + p2.x) / 2;
                    const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
                    const my   = (p1.y + p2.y) / 2 - Math.min(dist * 0.3, 60);
                    const arcD = `M${p1.x},${p1.y} Q${mx},${my} ${p2.x},${p2.y}`;

                    // ── Dashed arc line ──
                    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
                    path.setAttribute("d", arcD);
                    path.setAttribute("stroke", src.color);
                    path.setAttribute("stroke-width", "1.5");
                    path.setAttribute("fill", "none");
                    path.setAttribute("stroke-dasharray", "6 3");
                    path.style.animation = "arc-flow 0.8s linear infinite";
                    path.style.filter    = `drop-shadow(0 0 4px ${src.color})`;
                    path.style.opacity   = "0.9";
                    arcGroup.appendChild(path);

                    // ── Traveling glow dot along the arc (animateMotion) ──
                    const travDot = document.createElementNS("http://www.w3.org/2000/svg", "circle");
                    travDot.setAttribute("r", "3");
                    travDot.setAttribute("fill", src.color);
                    travDot.style.filter = `drop-shadow(0 0 5px ${src.color}) drop-shadow(0 0 10px ${src.color})`;
                    const motionEl = document.createElementNS("http://www.w3.org/2000/svg", "animateMotion");
                    motionEl.setAttribute("dur", "1.2s");
                    motionEl.setAttribute("repeatCount", "indefinite");
                    motionEl.setAttribute("path", arcD);
                    travDot.appendChild(motionEl);
                    arcGroup.appendChild(travDot);

                    // ── Sonar ripple rings at each endpoint ──
                    function addRipples(x, y, color) {
                        for (let i = 0; i < 3; i++) {
                            setTimeout(() => {
                                if (!arcGroup.isConnected) return;
                                const ring = document.createElementNS("http://www.w3.org/2000/svg", "circle");
                                ring.setAttribute("cx", x);
                                ring.setAttribute("cy", y);
                                ring.setAttribute("r", "4");
                                ring.setAttribute("fill", "none");
                                ring.setAttribute("stroke", color);
                                ring.setAttribute("stroke-width", "1.2");
                                ring.setAttribute("stroke-opacity", "0.85");
                                ring.style.filter = `drop-shadow(0 0 3px ${color})`;
                                arcGroup.appendChild(ring);
                                let r = 4, op = 0.85;
                                const expand = setInterval(() => {
                                    r  += 0.9;
                                    op -= 0.030;
                                    if (op <= 0 || r > 30) {
                                        clearInterval(expand);
                                        try { arcGroup.removeChild(ring); } catch (_) {}
                                    } else {
                                        ring.setAttribute("r", r.toFixed(1));
                                        ring.setAttribute("stroke-opacity", op.toFixed(3));
                                    }
                                }, 30);
                            }, i * 500);
                        }
                    }
                    addRipples(p1.x, p1.y, src.color);
                    addRipples(p2.x, p2.y, dst.color);

                    // ── Fade arc + dot together, then schedule next ──
                    setTimeout(() => {
                        let op = 0.9;
                        const fade = setInterval(() => {
                            op -= 0.07;
                            if (op <= 0) {
                                clearInterval(fade);
                                try { arcGroup.removeChild(path); } catch (_) {}
                                try { arcGroup.removeChild(travDot); } catch (_) {}
                                setTimeout(drawNextArc, 400);
                            } else {
                                path.style.opacity    = op.toString();
                                travDot.style.opacity = op.toString();
                            }
                        }, 30);
                    }, 2500);
                }

                chart.events.once("ready", () => {
                    const tooltipDom = chart.tooltipContainer?.dom;
                    if (tooltipDom?.parentNode) {
                        tooltipDom.parentNode.appendChild(tooltipDom);
                    }
                    drawNextArc();
                });
            }
            for (let s in allPoints) {
                let pin = { ...allPoints[s] };
                if (pin['city'] !== null) pin['plotCity'] = pin['city'];
                imgSeries.addData(pin);
                await sleep(14000 / 5);
                imgSeries.removeData(1);
            }
            initcallback();
        }
    }, [finalData1]);

    function disposeChart() {
        if (chart) { chart.dispose(); }
    }

    // ── Derived display data ──
    const summary      = displaylist["summary"] || {};
    const topAttackers = displaylist["Top Attacker's Infrastructure"] || [];
    const topMalware   = displaylist["Top Malware Types"] || [];

    // Feed: apply active category filter
    const feedPoints = activeFilter === 'All'
        ? allPoints
        : allPoints.filter(p => p.category === activeFilter);

    // Stats derived values
    const topCountry     = topAttackers[0]?.country || '—';
    const topCountryCode = topAttackers[0]?.country_code || '';

    // Country drilldown — computed each render when a country is selected
    let drillPanel = null;
    if (selectedCountry) {
        const pts = allPoints.filter(p => p.country_code === selectedCountry.id);
        const sevCounts = { critical: 0, high: 0, medium: 0, low: 0 };
        const strainCount = {};
        pts.forEach(p => {
            if (p.severity in sevCounts) sevCounts[p.severity]++;
            const k = p.strain || p.category || 'Unknown';
            strainCount[k] = (strainCount[k] || 0) + 1;
        });
        drillPanel = {
            pts,
            sevEntries: Object.entries(sevCounts).filter(([, v]) => v > 0),
            topStrains: Object.entries(strainCount).sort((a, b) => b[1] - a[1]).slice(0, 4),
        };
    }

    if (fetchError) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: '100vh', background: '#0d0005', flexDirection: 'column', gap: '16px'
            }}>
                <div style={{
                    border: '1px solid #c0182e', borderRadius: '8px', padding: '32px 48px',
                    background: '#1a0205', color: '#ff4455', textAlign: 'center', maxWidth: '420px'
                }}>
                    <div style={{ fontSize: '2rem', marginBottom: '12px' }}>&#9888;</div>
                    <div style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '8px' }}>
                        Threat Map Unavailable
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#cc8888' }}>{fetchError}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="tm-root">

            {/* ════════════════════════════════════════
                Brand bar — Logo | Title | LIVE + Clock
            ════════════════════════════════════════ */}
            <div className="tm-header">
                <div style={{ flex: 1, minWidth: 0 }}>
                    <a href="https://www.secneurx.com" target="_blank" rel="noreferrer">
                        <img className="tm-logo" src={logo} alt="logo" />
                    </a>
                </div>

                <div className="tm-title">CYBER THREAT MAP</div>

                <div className="tm-header-right">
                    {/* Try Free CTA — desktop */}
                    <a className="tm-cta-btn"
                       href="https://www.secneurx.com/threatintelligencefeeds"
                       target="_blank" rel="noreferrer">
                        Free Trial
                    </a>
                </div>
            </div>

            {/* ════════════════════════════════════════
                Stats bar — 4 counters (commented out)
            ════════════════════════════════════════ */}
            {false && <div className="tm-stats-bar">
                {[
                    { label: "THREATS DETECTED",   value: animatedStats.total.toLocaleString(),     color: "#ff3355" },
                    { label: "ACTIVE C2 SERVERS",  value: animatedStats.c2,                         color: "#ff8800" },
                    { label: "COUNTRIES AFFECTED",  value: animatedStats.countries,                 color: "#ffcc00" },
                ].map((stat, i) => (
                    <Fragment key={i}>
                        <div className="tm-stat-item">
                            <div className="tm-stat-label">{stat.label}</div>
                            <div className="tm-stat-value"
                                style={{ color: stat.color, textShadow: `0 0 12px ${stat.color}55` }}>
                                {stat.value}
                            </div>
                        </div>
                        <div className="tm-stat-divider" />
                    </Fragment>
                ))}
                {/* TOP COUNTRY — flag + name */}
                <div className="tm-stat-item">
                    <div className="tm-stat-label">TOP COUNTRY</div>
                    <div className="tm-stat-flag-row">
                        {topCountryCode && (
                            <ReactCountryFlag countryCode={topCountryCode} svg
                                style={{ width: '1.4em', height: '1.4em' }} />
                        )}
                        <span className="tm-stat-value"
                            style={{ color: "#ff9944", textShadow: "0 0 12px rgba(255,153,68,0.4)" }}>
                            {topCountry}
                        </span>
                    </div>
                </div>
            </div>}

            {/* ════════════════════════════════════════
                Map canvas — full main view on mobile, normal flex on desktop
            ════════════════════════════════════════ */}
            <div className="tm-map-container">
                <div id="chartdiv" />

                {/* Country drilldown panel — shown on polygon or attacker-row click */}
                {selectedCountry && drillPanel && (
                    <div className="tm-country-drill">
                        <div className="tm-country-drill-header">
                            <div className="tm-country-drill-title">
                                {selectedCountry.id && (
                                    <ReactCountryFlag countryCode={selectedCountry.id} svg
                                        style={{ width: '1.3em', height: '1.3em', flexShrink: 0 }} />
                                )}
                                <span>{selectedCountry.name}</span>
                            </div>
                            <button className="tm-country-drill-close" onClick={() => setSelectedCountry(null)}>✕</button>
                        </div>
                        <div className="tm-country-drill-body">
                            <div className="tm-country-drill-total">
                                {drillPanel.pts.length > 0
                                    ? `${drillPanel.pts.length} threat${drillPanel.pts.length !== 1 ? 's' : ''} detected`
                                    : 'No threats in current dataset'}
                            </div>
                            {drillPanel.sevEntries.length > 0 && (
                                <div className="tm-country-drill-sevs">
                                    {drillPanel.sevEntries.map(([sev, cnt]) => (
                                        <span key={sev} className="tm-feed-severity-badge"
                                            style={{ background: SEVERITY_COLORS[sev], color: '#000' }}>
                                            {sev}: {cnt}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {drillPanel.topStrains.length > 0 && (
                                <div className="tm-country-drill-strains">
                                    {drillPanel.topStrains.map(([strain, cnt]) => (
                                        <div key={strain} className="tm-country-drill-strain-row">
                                            <span className="tm-country-drill-strain-name">{strain}</span>
                                            <span className="tm-country-drill-strain-cnt">{cnt}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Mobile-only: floating stats button — hidden when sheet is open */}
                {!statsModalOpen && (
                    <button className="tm-mobile-stats-btn" onClick={() => setStatsModalOpen(true)}>
                        📊&ensp;Stats
                    </button>
                )}
            </div>

            {/* ════════════════════════════════════════
                Bottom panels — 3 columns (modal on mobile)
            ════════════════════════════════════════ */}
            <div className={`tm-bottom${statsModalOpen ? ' tm-stats-modal-active' : ''}`}>
                {/* Drag handle — swipe down to dismiss */}
                <div
                    className="tm-sheet-handle"
                    onTouchStart={e => { touchStartY.current = e.touches[0].clientY; }}
                    onTouchEnd={e => {
                        if (e.changedTouches[0].clientY - touchStartY.current > 60)
                            setStatsModalOpen(false);
                    }}
                >
                    <div className="tm-sheet-handle-bar" />
                </div>

                {/* ── Panel 1: Top Attacker Origins ── */}
                <div className="tm-panel tm-panel-attackers">
                    <div className="tm-panel-header">
                        <div className="tm-panel-header-left">
                            <div className="tm-panel-accent" style={{ background: "#ff4444" }} />
                            <span className="tm-panel-title">Top Attacker Origins</span>
                        </div>
                        {/* Close button sits in header so it aligns with title text */}
                        <button className="tm-stats-modal-close" onClick={() => setStatsModalOpen(false)}>✕</button>
                    </div>
                    <div className="tm-attacker-list">
                        {topAttackers.slice(0, 5).map((row, idx) => (
                            <div key={idx} className="tm-attacker-row tm-attacker-row-clickable"
                                onClick={() => setSelectedCountry(prev =>
                                    prev?.id === row.country_code ? null : { id: row.country_code, name: row.country }
                                )}>
                                <ReactCountryFlag
                                    countryCode={row.country_code}
                                    svg
                                    style={{ width: '1.5em', height: '1.5em', flexShrink: 0 }}
                                    title={row.country_code}
                                />
                                <div className="tm-attacker-info">
                                    <div className="tm-attacker-name-row">
                                        <span className="tm-attacker-country">{row.country}</span>
                                        <span className="tm-attacker-pct">{row.percentage}%</span>
                                    </div>
                                    <div className="tm-bar-track">
                                        <div className="tm-bar-fill" style={{ width: `${row.percentage}%` }} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Panel 2: Live Threat Feed ── */}
                <div className="tm-panel tm-panel-feed">
                    <div className="tm-panel-header">
                        <div className="tm-panel-header-left">
                            <div className="tm-panel-accent" style={{ background: "#ff6622" }} />
                            <span className="tm-panel-title">Live Threat Feed</span>
                        </div>
                    </div>
                    {/* feedBodyRef measures the scrollable area height for the slider animation */}
                    <div className="tm-panel-body" ref={feedBodyRef}>
                        <div
                            className="slider"
                            style={{
                                '--feed-panel-h': `${feedBodyH}px`,
                                animationDuration: `${Math.max(feedPoints.length * 3, 10)}s`,
                            }}
                        >
                            {feedPoints.map((row, i) => {
                                const catColor    = topMalware.find(m => m.category === row.category)?.color || '#888888';
                                const cityDisplay = row.cityRaw || (row.city ? row.city.replace(/[()]/g, '') : null);
                                const strainLabel = (row.strain && row.strain.toLowerCase() !== row.category?.toLowerCase())
                                    ? row.strain : `Generic ${row.category || 'Threat'}`;
                                const sevColor = row.severityColor || '#888888';
                                const sevLabel = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' }[row.severity] || '—';
                                return (
                                    <div key={i} className="tm-feed-row">
                                        {/* Col 1: Location — flag + country, city inline */}
                                        <div className="tm-feed-col-location">
                                        {row.country_code && (
                                                <ReactCountryFlag countryCode={row.country_code} svg
                                                    style={{ width: '1.2em', height: '1.2em', flexShrink: 0 }} />
                                            )}
                                            <span className="tm-feed-country-name">{row.country || '—'}</span>
                                            {cityDisplay && (
                                                <span className="tm-feed-city">{`(${cityDisplay})`}</span>
                                            )}
                                        </div>
                                        {/* Col 2: Strain name */}
                                        <span className="tm-feed-strain">{strainLabel}</span>
                                        {/* Col 3: Severity badge — matches tooltip card style */}
                                        <div className="tm-feed-col-severity">
                                            <span className="tm-feed-severity-badge" style={{
                                                background: sevColor,
                                                color: '#000',
                                            }}>
                                                {sevLabel}
                                            </span>
                                        </div>
                                        {/* Col 4: Category badge */}
                                        <div className="tm-feed-col-category">
                                            <span className="tm-feed-badge" style={{
                                                background: `${catColor}22`,
                                                border: `1px solid ${catColor}88`,
                                                color: catColor,
                                            }}>
                                                {row.category}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* ── Panel 3: Threat Distribution ── */}
                <div className="tm-panel tm-panel-distribution">
                    <div className="tm-panel-header">
                        <div className="tm-panel-header-left">
                            <div className="tm-panel-accent" style={{ background: "#cc1128" }} />
                            <span className="tm-panel-title">Threat Distribution</span>
                        </div>
                    </div>
                    <div className="tm-dist-body">
                        <DonutChart segments={topMalware} total={summary.total_events || 0} />
                        <div className="tm-legend-list">
                            {topMalware.map((item, idx) => (
                                <div key={idx} className="tm-legend-row">
                                    <div className="tm-legend-dot-name">
                                        <div className="tm-legend-dot" style={{ background: item.color }} />
                                        <span className="tm-legend-category">{item.category}</span>
                                    </div>
                                    <span className="tm-legend-count">
                                        {item.count}&ensp;<span className="tm-legend-pct">({item.percentage}%)</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>


            </div>

        </div>
    );
}
